import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Activity,
  ArrowLeft,
  ArrowRight,
  EyeOff,
  HeartHandshake,
  PartyPopper,
  Smartphone,
  UserRound,
} from "lucide-react";
import { usePageMeta } from "../hooks/usePageMeta";
import { useSettings } from "../hooks/useSettings";
import { ctaState } from "../lib/cta";
import { track } from "../lib/analytics";
import { supabase } from "../lib/supabase";
import { StoreBadges } from "../components/ui/StoreBadges";
import { Spark } from "../components/ui/decor";

// Bounce page for "Invite a friend" links (https://usefitbuddy.com/invite/<token>).
// Hands the token to the app via weighearnapp://invite/<token>, where the two
// people are connected as friends. Private per-person URL, so never indexed.
// Shows who sent the invite (name + photo) via friend_invite_preview, which
// only answers for a live link and never returns friends-only data.
const TOKEN_RE = /^[0-9a-f]{16}$/i;
const APP_SCHEME = "weighearnapp";
const ANDROID_PACKAGE = "com.calbank01.weighearnapp";

type Platform = "android" | "ios" | "desktop";

/** null while loading. "unknown" = we couldn't ask (offline, no backend):
 * the page falls back to the generic "your friend" copy and still works. */
type Preview =
  | { status: "ok"; name: string | null; avatarUrl: string | null; since: string | null }
  | { status: "expired" | "invalid" | "unknown" };

function detectPlatform(): Platform {
  if (typeof navigator === "undefined") return "desktop";
  const ua = navigator.userAgent;
  if (/android/i.test(ua)) return "android";
  // iPadOS 13+ reports as Macintosh; touch points give it away.
  if (/iphone|ipad|ipod/i.test(ua) || (/macintosh/i.test(ua) && navigator.maxTouchPoints > 1)) {
    return "ios";
  }
  return "desktop";
}

function openHref(token: string, platform: Platform, googlePlayUrl: string): string {
  if (platform === "android") {
    const fallback = googlePlayUrl
      ? `S.browser_fallback_url=${encodeURIComponent(googlePlayUrl)};`
      : "";
    return `intent://invite/${token}#Intent;scheme=${APP_SCHEME};package=${ANDROID_PACKAGE};${fallback}end`;
  }
  return `${APP_SCHEME}://invite/${token}`;
}

async function loadPreview(token: string): Promise<Preview> {
  if (!supabase) return { status: "unknown" };
  try {
    const { data, error } = await supabase.rpc("friend_invite_preview", { invite_token: token });
    const row = (data as
      | { status: string; display_name: string | null; avatar_url: string | null; member_since: string | null }[]
      | null)?.[0];
    if (error || !row) return { status: "unknown" };
    if (row.status === "ok") {
      return {
        status: "ok",
        name: row.display_name?.trim() || null,
        avatarUrl: row.avatar_url || null,
        since: row.member_since,
      };
    }
    if (row.status === "expired" || row.status === "invalid") return { status: row.status };
    return { status: "unknown" };
  } catch {
    return { status: "unknown" };
  }
}

function initials(name: string | null): string {
  if (!name) return "";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? "") + (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase();
}

function memberSince(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
}

/** The inviter's face: their photo, or their initials on teal if they have
 * no photo (or it fails to load). */
function InviterAvatar({ name, url }: { name: string | null; url: string | null }) {
  const [broken, setBroken] = useState(false);
  const letters = initials(name);
  return (
    <div className="relative h-28 w-28 shrink-0 sm:h-36 sm:w-36">
      <span
        aria-hidden
        className="absolute inset-0 rounded-full bg-teal/40 motion-safe:animate-pulse-ring"
      />
      <div className="relative h-full w-full overflow-hidden rounded-full border-[3px] border-navy bg-teal shadow-pop-sm">
        {url && !broken ? (
          <img
            src={url}
            alt={name ? `${name}'s profile photo` : "Your friend's profile photo"}
            referrerPolicy="no-referrer"
            width={144}
            height={144}
            className="h-full w-full object-cover"
            onError={() => setBroken(true)}
          />
        ) : (
          <div
            role="img"
            aria-label={name ? `${name}'s initials` : "Your friend"}
            className="flex h-full w-full items-center justify-center font-display text-4xl font-extrabold text-white sm:text-5xl"
          >
            {letters || <UserRound aria-hidden className="h-12 w-12" />}
          </div>
        )}
      </div>
    </div>
  );
}

/** Inviter + you, joined by a handshake: the connection you're one tap from. */
function ConnectGraphic({ name, url }: { name: string | null; url: string | null }) {
  return (
    <div className="flex items-center justify-center gap-2 sm:gap-4">
      <InviterAvatar name={name} url={url} />
      <div aria-hidden className="relative flex min-w-0 flex-1 items-center sm:max-w-[9rem]">
        <span className="h-0 w-full border-t-[3px] border-dashed border-navy/40" />
        <span className="absolute left-1/2 top-1/2 flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-[3px] border-navy bg-sun shadow-pop-xs motion-safe:animate-wiggle">
          <HeartHandshake className="h-5 w-5 text-navy" />
        </span>
      </div>
      <div
        aria-hidden
        className="flex h-24 w-24 shrink-0 items-center justify-center rounded-full border-[3px] border-dashed border-navy bg-white font-display text-xl font-extrabold text-navy sm:h-28 sm:w-28 sm:text-2xl"
      >
        You
      </div>
    </div>
  );
}

const PERKS = [
  {
    icon: Activity,
    title: "See each other's days",
    body: "Spot when they've logged a weigh-in, a workout or their food, and they'll see yours.",
  },
  {
    icon: PartyPopper,
    title: "Cheer the wins",
    body: "A new low or a long streak lands better when someone's there to notice it.",
  },
  {
    icon: EyeOff,
    title: "You choose what they see",
    body: "Weights and numbers stay private unless you switch them on. Change it any time.",
  },
];

export default function Invite() {
  usePageMeta({
    title: "You've been invited to FitBuddy | FitBuddy",
    description:
      "A friend has invited you to FitBuddy. Open the link on your phone to connect straight away, then cheer each other on as you bank calories together.",
    path: "/invite",
    noindex: true,
  });

  const { token: raw = "" } = useParams<{ token: string }>();
  const token = TOKEN_RE.test(raw) ? raw.toLowerCase() : null;
  const s = useSettings();
  const { live, target } = ctaState(s);
  const platform = useMemo(detectPlatform, []);
  const href = token ? openHref(token, platform, s.google_play_url) : "";

  const [preview, setPreview] = useState<Preview | null>(token ? null : { status: "invalid" });
  useEffect(() => {
    if (!token) return;
    let alive = true;
    void loadPreview(token).then((p) => {
      if (alive) setPreview(p);
    });
    return () => {
      alive = false;
    };
  }, [token]);

  // Android only: try the app once straight away. iOS Safari shows a "cannot
  // open page" alert when the app isn't installed, so there we wait for a tap.
  const tried = useRef(false);
  const newHere = useRef<HTMLDivElement>(null);
  const [noApp, setNoApp] = useState(false);

  // The token is private: don't hand it to store pages or other sites in the
  // Referer header when someone taps out of this page.
  useEffect(() => {
    const el = document.createElement("meta");
    el.setAttribute("name", "referrer");
    el.setAttribute("content", "no-referrer");
    document.head.appendChild(el);
    return () => {
      el.remove();
    };
  }, []);

  // If the app opened, this page goes to the background. Still showing a
  // moment later means FitBuddy isn't installed yet: point at the download.
  const onOpen = () => {
    track("invite_open_click", { platform });
    window.setTimeout(() => {
      if (document.visibilityState !== "visible") return;
      setNoApp(true);
      newHere.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 1600);
  };
  useEffect(() => {
    if (!token || platform !== "android" || tried.current) return;
    tried.current = true;
    track("invite_auto_open", { platform });
    window.location.href = href;
    // Run once on mount only; later settings loads must not re-trigger it.
  }, []);

  const loading = preview === null;
  const dead = preview?.status === "expired" || preview?.status === "invalid";
  const name = preview?.status === "ok" ? preview.name : null;
  const firstName = name?.split(/\s+/)[0] ?? null;
  const avatarUrl = preview?.status === "ok" ? preview.avatarUrl : null;
  const since = preview?.status === "ok" ? memberSince(preview.since) : null;
  const them = firstName ?? "your friend";

  return (
    <section className="relative isolate overflow-hidden pt-32 pb-24">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute left-1/2 top-40 h-80 w-80 -translate-x-1/2 rounded-full bg-teal/15 blur-3xl" />
        <div className="absolute -right-24 top-[28rem] h-72 w-72 rounded-full bg-violet/10 blur-3xl" />
      </div>
      <div className="container-x max-w-2xl">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-teal-deep hover:underline"
        >
          <ArrowLeft className="h-4 w-4" /> Back to FitBuddy
        </Link>

        {loading ? (
          <div aria-busy="true" aria-label="Loading your invite" className="mt-8">
            <div className="card-pop p-6 sm:p-8">
              <div className="flex items-center justify-center gap-4">
                <div className="h-28 w-28 animate-pulse rounded-full bg-navy/10 sm:h-36 sm:w-36" />
                <div className="h-1 w-16 rounded bg-navy/10" />
                <div className="h-24 w-24 animate-pulse rounded-full bg-navy/10 sm:h-28 sm:w-28" />
              </div>
              <div className="mx-auto mt-8 h-9 w-4/5 animate-pulse rounded-xl bg-navy/10" />
              <div className="mx-auto mt-3 h-4 w-3/5 animate-pulse rounded bg-navy/10" />
            </div>
          </div>
        ) : dead ? (
          <>
            <h1 className="display mt-6 break-words text-3xl text-navy sm:text-5xl">
              {preview?.status === "expired"
                ? "This invite has expired"
                : token
                  ? "This invite isn't working"
                  : "This invite link looks incomplete"}
            </h1>
            <p className="mt-4 text-charcoal/75">
              {preview?.status === "expired"
                ? "Invite links last 30 days. Ask your friend to send you a fresh one and you'll be connected in a tap."
                : "Ask your friend to send it again. Once you've got the full link, tap it and you'll be connected."}
            </p>
          </>
        ) : (
          <>
            <div className="relative mt-8 card-pop px-5 pb-8 pt-10 text-center sm:px-10">
              <span className="sticker absolute -top-5 left-1/2 -translate-x-1/2 whitespace-nowrap">
                Friend invite
              </span>
              <Spark fill="#8B5CF6" className="absolute right-5 top-5 !h-6 !w-6" />
              <ConnectGraphic name={name} url={avatarUrl} />
              {name && (
                <p className="mt-5 font-display text-lg font-extrabold text-navy">
                  {name}
                  {since && (
                    <span className="mt-0.5 block font-body text-sm font-medium text-charcoal/70">
                      On FitBuddy since {since}
                    </span>
                  )}
                </p>
              )}
              <h1 className="display mt-5 break-words text-3xl leading-tight text-navy sm:text-5xl">
                {firstName ? (
                  <>
                    <span className="marker">{firstName}</span> wants you on FitBuddy
                  </>
                ) : (
                  "Your friend wants you on FitBuddy"
                )}
              </h1>
              <p className="mx-auto mt-4 max-w-md text-charcoal/75">
                Tap below to open FitBuddy and you and {them} will be friends
                straight away, so you can cheer each other on.
              </p>

              {platform === "desktop" && (
                <p className="mt-6 flex items-start gap-3 rounded-2xl bg-teal/10 p-4 text-left text-charcoal/80">
                  <Smartphone aria-hidden className="mt-0.5 h-5 w-5 shrink-0 text-teal-deep" />
                  <span>
                    This link works on your phone. Open it there, from the message
                    {firstName ? ` ${firstName}` : " your friend"} sent you, and
                    you'll be connected in a tap.
                  </span>
                </p>
              )}
              {/* A phone-only deep link: on a computer there is nothing to open. */}
              {platform !== "desktop" && (
                <div className="mt-7">
                  <a
                    href={href}
                    onClick={onOpen}
                    className="btn-primary group w-full px-7 py-3.5 text-base sm:w-auto"
                  >
                    {firstName ? `Connect with ${firstName}` : "Open FitBuddy"}
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </a>
                </div>
              )}
            </div>

            <h2 className="mt-14 font-display text-2xl font-extrabold text-navy">
              What you get as friends
            </h2>
            <ul className="mt-5 grid gap-4 sm:grid-cols-3">
              {PERKS.map(({ icon: Icon, title, body }) => (
                <li key={title} className="glass p-5">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full border-2 border-navy bg-teal-mist">
                    <Icon aria-hidden className="h-5 w-5 text-teal-deep" />
                  </span>
                  <h3 className="mt-3 font-display text-base font-extrabold text-navy">{title}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-charcoal/75">{body}</p>
                </li>
              ))}
            </ul>
          </>
        )}

        <div
          ref={newHere}
          className={`mt-12 card-pop p-6 sm:p-8${noApp ? " ring-4 ring-teal/40" : ""}`}
        >
          <h2 className="font-display text-xl font-semibold text-navy">
            New to FitBuddy?
          </h2>
          {noApp && (
            <p className="mt-3 leading-relaxed text-charcoal/80">
              Looks like you don't have FitBuddy yet. Three quick steps and you're in.
            </p>
          )}
          <ol className="mt-4 space-y-3">
            {[
              "Download FitBuddy from your app store.",
              "Create your account.",
              dead
                ? "Tap a fresh invite link from your friend and you'll be connected."
                : `Tap the invite link in your messages again and you'll be friends with ${them}.`,
            ].map((step, i) => (
              <li key={i} className="flex items-start gap-3 text-charcoal/80">
                <span className="num flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 border-navy bg-sun text-sm font-bold text-navy">
                  {i + 1}
                </span>
                <span className="pt-0.5 leading-relaxed">{step}</span>
              </li>
            ))}
          </ol>
          <div className="mt-6">
            <StoreBadges
              appStoreUrl={s.app_store_url}
              googlePlayUrl={s.google_play_url}
              live={live}
              source="invite"
              fallbackId={target}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
